import React from 'react';
import { FileText } from 'lucide-react';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">iOS PreCheck</span>
            </div>
            <a href="/" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors">Back to Home</a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Terms of Service</h1>
          </div>
          
          <p className="text-gray-600 mb-8">Last Updated: January 2025</p>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Agreement to Terms</h2>
            <p className="text-gray-700 mb-6">
              By using iOS PreCheck ("Service"), you agree to these Terms of Service. If you don't agree, don't use the Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Service Description</h2>
            <p className="text-gray-700 mb-4">iOS PreCheck analyzes iOS .ipa files to identify potential App Store compliance issues including:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Private API usage detection</li>
              <li>Info.plist validation</li>
              <li>Privacy permission checks</li>
              <li>Asset validation</li>
              <li>Binary analysis</li>
              <li>Metadata review</li>
            </ul>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <p className="text-gray-900 font-semibold">Important:</p>
              <p className="text-gray-700">Our analysis is automated and provided for informational purposes only. It does not guarantee App Store approval.</p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Pricing</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Current Price:</strong> $2.99 per scan</li>
              <li>Prices may change with 30 days notice on our website</li>
              <li>All prices in USD</li>
              <li>Payment processed securely by Stripe</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What You Get</h2>
            <p className="text-gray-700 mb-4">For each paid scan, you receive:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>One complete compliance analysis of your .ipa file</li>
              <li>Detailed report of issues found</li>
              <li>Actionable recommendations for fixes</li>
              <li>Instant results (typically 30-60 seconds)</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What We Don't Guarantee</h2>
            <p className="text-gray-700 mb-4">We provide analysis tools, but we do NOT guarantee:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Your app will be approved by Apple</li>
              <li>Our analysis catches every possible issue</li>
              <li>Apple won't reject your app for other reasons</li>
              <li>Our recommendations will solve all problems</li>
            </ul>
            <p className="text-gray-700 mb-6 italic">
              Apple's review process is subjective and can change without notice.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Refund Policy</h2>
            <p className="text-gray-700 mb-4">We offer refunds in these situations:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Technical failure preventing analysis completion</li>
              <li>Service was unavailable after payment</li>
              <li>Duplicate charges</li>
            </ul>
            <p className="text-gray-700 mb-4 font-semibold">We do NOT refund if:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>You don't like the results</li>
              <li>Apple rejects your app despite our analysis</li>
              <li>You changed your mind after seeing the report</li>
              <li>The analysis found no issues (that's good news!)</li>
            </ul>
            <p className="text-gray-700 mb-6">
              To request a refund: Email support@iosprecheck.com within 7 days of purchase with your transaction ID.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Your Responsibilities</h2>
            <p className="text-gray-700 mb-4">You agree to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Only upload .ipa files you own or have permission to analyze</li>
              <li>Not upload malicious or harmful files</li>
              <li>Not reverse engineer or abuse our service</li>
              <li>Not attempt to circumvent payment</li>
              <li>Not resell or redistribute our analysis reports</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Prohibited Use</h2>
            <p className="text-gray-700 mb-4">You may NOT:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Use the service for illegal purposes</li>
              <li>Upload files containing malware or viruses</li>
              <li>Attempt to overwhelm our servers (DDoS)</li>
              <li>Share login credentials (if we add accounts)</li>
              <li>Scrape or automate requests beyond normal use</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Intellectual Property</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>You retain ownership of your .ipa files</li>
              <li>We retain ownership of our analysis methodology and code</li>
              <li>Analysis reports are provided for your personal use only</li>
              <li>You may not redistribute our technology or reports commercially</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Data and Privacy</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Your .ipa files are deleted within 1 hour of upload</li>
              <li>We don't store your app's source code</li>
              <li>Payment data is handled by Stripe (see their terms)</li>
              <li>See our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> for full details</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Limitation of Liability</h2>
            <div className="bg-gray-100 border border-gray-300 p-6 mb-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-3">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <p className="text-gray-700 mb-4">iOS PreCheck and HECANO LLC are not liable for:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>App Store rejections</li>
                <li>Lost revenue from delayed launches</li>
                <li>Indirect, consequential, or punitive damages</li>
                <li>Losses exceeding the amount you paid ($2.99)</li>
              </ul>
            </div>
            <p className="text-gray-700 mb-6">
              Our analysis is a tool to help identify issues. Final responsibility for App Store compliance rests with you.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Disclaimer of Warranties</h2>
            <p className="text-gray-700 mb-6">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Apple Relationship</h2>
            <p className="text-gray-700 mb-6">
              iOS PreCheck is not affiliated with, endorsed by, or officially connected to Apple Inc. We are an independent third-party service. "iOS" is a trademark of Apple Inc.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Contact</h2>
            <p className="text-gray-700 mb-4">Questions about these terms?</p>
            <ul className="list-none text-gray-700 mb-6 space-y-2">
              <li>Email: support@iosprecheck.com</li>
              <li>Company: HECANO LLC</li>
            </ul>

            <div className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>By using iOS PreCheck, you acknowledge that:</strong> You have read these Terms, you understand them, and you agree to be bound by them.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;